import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as RadixDialog from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useState, useEffect, useRef } from 'react';
import Draggable from 'react-draggable';
import { styled } from 'styletron-react';
import { staticColors } from '../../../const/colors/staticColors';
import { CSS_VARIABLE_REMOVED_SCROLL_BAR } from '../../../const/cssVariables';
import { sizes } from '../../../const/sizes';
import { getSpacing } from '../../../const/spacing';
import { transitionDuration } from '../../../const/transitionDuration';
import { transitionTimingFunction } from '../../../const/transitionTimingFunction';
import { zIndexes } from '../../../const/zIndexes';
import { useTestIdAttribute } from '../../../hooks/useTestIdAttribute';
import { CardContainer } from '../../Card/CardContainer';
import { Text } from '../../Text/Text';
import { Box } from '../../layout/Box';
import { FlexContainer } from '../../layout/FlexContainer';
import { RelativeContainer } from '../../layout/RelativeContainer';
import { DialogTitleRightControls, DIALOG_DRAG_HANDLE_CLASS, } from './DialogTitleRightControls';
import { dialogSizeMap } from './dialogConst';
const transition = {
    transitionProperty: 'opacity',
    transitionDuration: transitionDuration.short,
    transitionTimingFunction: transitionTimingFunction.ease,
};
const StyledOverlay = styled(RadixDialog.Overlay, (p) => ({
    ...transition,
    opacity: p.$visible ? 1 : 0,
    position: 'fixed',
    backgroundColor: staticColors.overlayBackground,
    backdropFilter: `blur(${sizes.dialog.overlayBlur})`,
    inset: 0,
    zIndex: zIndexes.dialog,
}));
const StyledCenteringWrapper = styled('div', {
    position: 'fixed',
    inset: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
    zIndex: zIndexes.dialog,
});
const StyledContent = styled(RadixDialog.Content, (p) => ({
    ...transition,
    opacity: p.$visible ? 1 : 0,
    background: 'none',
    pointerEvents: 'auto',
    ':focus': {
        outline: 'none',
    },
    paddingRight: `var(${CSS_VARIABLE_REMOVED_SCROLL_BAR}, 0)`,
}));
const CARD_PADDING = getSpacing('2');
const OUTSIDE_PADDING_COUNT = 2;
const MAX_WIDTH = `calc(100vw - (${CARD_PADDING} * ${OUTSIDE_PADDING_COUNT}))`;
const CARD_AND_OUTSIDE_PADDING_COUNT = 4;
const MAX_HEIGHT = `calc(100vh - (${CARD_PADDING} * ${CARD_AND_OUTSIDE_PADDING_COUNT}))`;
const cardContentSpacing = {
    spacingTop: '2',
    spacingHorizontal: '2',
};
export const Dialog = ({ children, footer, title, titleRightElement, open, size = 'md', minHeight: minHeightPassed, testId, onOutsideClick, onClose, draggable = false, }) => {
    const testIdAttribute = useTestIdAttribute(testId);
    const [visible, setVisible] = useState(false);
    const [cachedContentUntilCloseTransition, setCachedContentUntilCloseTransition,] = useState();
    const contentRef = useRef(null);
    const [draggableKey, setDraggableKey] = useState(0);
    const width = dialogSizeMap[size];
    const minHeight = minHeightPassed
        ? `min(${minHeightPassed}, ${MAX_HEIGHT})`
        : undefined;
    const handleOpenChange = (newOpen) => {
        if (newOpen === false) {
            onOutsideClick?.();
        }
    };
    const handlePointerDownOutside = (e) => {
        if (!onOutsideClick)
            e.preventDefault();
    };
    const handleTransitionEnd = () => {
        if (!visible)
            setCachedContentUntilCloseTransition(undefined);
    };
    const prevOpenRef = useRef(false);
    useEffect(() => {
        if (open) {
            setVisible(true);
            setCachedContentUntilCloseTransition({ title, children, footer });
            if (!prevOpenRef.current) {
                setDraggableKey((k) => k + 1);
            }
        }
        else {
            setVisible(false);
        }
        prevOpenRef.current = open;
    }, [open, title, children, footer]);
    if (!open && !cachedContentUntilCloseTransition)
        return null;
    const hasTitleText = !!cachedContentUntilCloseTransition?.title;
    const hasDragOrClose = draggable || !!onClose;
    const hasTitle = hasTitleText || !!titleRightElement || hasDragOrClose;
    const titleHorizontalAlign = hasTitleText
        ? 'spaceBetween'
        : 'right';
    const titleSpacingBottom = hasTitle ? '2' : '0';
    const maybeTitleText = hasTitleText ? (_jsx(Text, { variant: 'h3', children: cachedContentUntilCloseTransition.title })) : null;
    const maybeTitle = hasTitle ? (_jsx(RadixDialog.Title, { asChild: true, children: _jsxs(FlexContainer, { direction: 'row', horizontalAlign: titleHorizontalAlign, spacingBottom: titleSpacingBottom, children: [maybeTitleText, _jsx(DialogTitleRightControls, { draggable: draggable, titleRightElement: titleRightElement, onClose: onClose })] }) })) : (_jsx(VisuallyHidden, { asChild: true, children: _jsx(RadixDialog.Title, { children: "No title" }) }));
    const contentSpacingBottom = cachedContentUntilCloseTransition?.footer ? '0' : '2';
    const maybeFooter = cachedContentUntilCloseTransition?.footer ? (_jsx(Box, { spacingVertical: '2', children: cachedContentUntilCloseTransition.footer })) : null;
    // Prevents the autofocus on the first tooltip in dialog
    const preventDefaultOnOpenAutoFocus = (e) => e.preventDefault();
    return (_jsx(RadixDialog.Root, { open: true, modal: true, onOpenChange: handleOpenChange, children: _jsxs(RadixDialog.Portal, { children: [_jsx(StyledOverlay, { $visible: visible, onTransitionEnd: handleTransitionEnd }), _jsx(StyledCenteringWrapper, { children: _jsx(Draggable, { nodeRef: contentRef, handle: `.${DIALOG_DRAG_HANDLE_CLASS}`, bounds: 'parent', disabled: !draggable, defaultPosition: { x: 0, y: 0 }, children: _jsx(StyledContent, { ref: contentRef, $visible: visible, "aria-describedby": undefined, onPointerDownOutside: handlePointerDownOutside, ...testIdAttribute, onOpenAutoFocus: preventDefaultOnOpenAutoFocus, children: _jsx(FlexContainer, { width: width, maxWidth: MAX_WIDTH, children: _jsx(RelativeContainer, { fullWidth: true, children: _jsx(CardContainer, { cardVariant: 'white', fullWidth: true, children: _jsxs(FlexContainer, { direction: 'column', maxHeight: MAX_HEIGHT, minHeight: minHeight, ...cardContentSpacing, children: [maybeTitle, _jsx(Box, { overflow: 'auto', grow: 1, spacingBottom: contentSpacingBottom, children: cachedContentUntilCloseTransition?.children }), maybeFooter] }) }) }) }) }) }, draggableKey) })] }) }));
};
